import type { SalesOrderLotDebtSummary } from '@/lib/api/sales-order-api'
import { Badge } from '@/components/ui/badge'
import { m } from '@/lib/paraglide/messages'
import { serverNow } from '@/lib/server-time'

/**
 * Thời gian đơn đã chờ nhập bù lô, dạng "3h 5m" / "18m".
 *
 * Dùng serverNow() chứ không Date.now() vì máy trạm ở quầy hay lệch giờ — cùng
 * lý do SLABadge dùng nó: đồng hồ lệch không được bịa ra số giờ chờ.
 */
export function formatWaited(oldestOpenAt: string): string {
    // Ngày sai định dạng cho NaN, và mọi phép tính sau đó ra "NaNm" trên badge.
    // Bỏ hẳn phần thời gian chờ còn hơn hiện chữ vô nghĩa cạnh số nợ.
    const at = new Date(oldestOpenAt).getTime()
    if (!Number.isFinite(at)) return ''
    const diff = Math.max(0, serverNow() - at)
    const h = Math.floor(diff / 3_600_000)
    const min = Math.floor((diff % 3_600_000) / 60_000)
    return h > 0 ? `${h}h ${min}m` : `${min}m`
}

interface Props {
    debt: SalesOrderLotDebtSummary | null | undefined
}

/**
 * Cột "Nợ hàng" ở danh sách đơn (FD-372): đơn bán tại quầy khi kho chưa có lô
 * thật thì treo nợ, kho phải nhập bù.
 *
 * Chỉ hiện thời gian chờ chứ KHÔNG tự đặt ngưỡng đỏ/vàng: mốc nhắc lại nằm ở
 * station_voice_settings và sửa được, gắn cứng một con số thứ hai ở FE là chắc
 * chắn sẽ lệch với loa.
 */
export function OrderLotDebtBadge({ debt }: Props) {
    // Đếm số LOẠI sản phẩm, không cộng số lượng: cột hẹp, mà cộng Kg với Gói
    // thành một số thì vừa sai vừa không nói lên điều gì. Chi tiết từng mặt
    // hàng xem ở popup lúc bán và ở màn nhập kho.
    const count = debt?.items.length ?? 0
    if (count === 0) return null

    const waited = debt?.oldest_open_at ? formatWaited(debt.oldest_open_at) : ''
    // formatWaited trả '' cho mốc hỏng — rơi về biến thể không có thời gian chờ.
    return (
        <Badge
            data-testid="order-lot-debt-badge"
            className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
        >
            {waited
                ? m.order_lot_debt_badge_waited({
                      count: String(count),
                      waited,
                  })
                : m.order_lot_debt_badge({ count: String(count) })}
        </Badge>
    )
}
